import { appendSearchParams } from '@alexis/helpers/searchParam';
import { uniq } from 'lodash-es';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { clsx } from '@wego/alexis/helpers/clsx';
import { translateNumber, translateText } from '@wego/alexis/helpers/translation';

import NoResults from '@components/NoResults';
import Price from '@components/Price';
import Promo from '@components/Promo';

import { ENHANCED_HOTEL_RATES } from '@constants/hotels';

import { GlobalContext } from '@context/GlobalContext';

import { notUndefinedAndNull } from '@helpers/array';
import { impressionLogHotelDetail, impressionLogOnHotelRateClick } from '@helpers/genzo';
import { getDailyVersion, sortHotelRates } from '@helpers/hotel';
import { cloudinaryImageTransformations } from '@helpers/imageTransformations';
import { convertHotelRatePriceToPrice } from '@helpers/price';
import { hotelsRoute } from '@helpers/routeTranslation';
import { webEngageTrackPurchase, webEngageTrackRateClick } from '@helpers/webEngage';

import PersonIcon from '@icons/adult.svg';
import Chevron from '@icons/chevron-down-medium.svg';
import SearchIcon from '@icons/search.svg';

import { changeIsIncludingTaxOrFee } from '@redux/actions/priceOptionsActions';
import {
  getCurrencyState,
  getCurrentSiteState,
  getExchangeRateState,
  getHotelMetaSearchState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import type { CurrentSite } from '@wegoTypes/currentSite';
import type { HotelBookingRoomTypes } from '@wegoTypes/hotelBooking/hotelRoomTypes';
import { Hotel } from '@wegoTypes/hotels/hotel';
import { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';
import { HotelPromo } from '@wegoTypes/hotels/metasearch/hotelPromo';
import { HotelRateAmenity } from '@wegoTypes/hotels/metasearch/hotelRateAmenity';
import type { MetasearchHotelRate } from '@wegoTypes/hotels/metasearch/metasearchHotelRate';
import { Room } from '@wegoTypes/hotels/metasearch/room';

import styles from './HotelDeals.module.scss';

export interface HotelDealsProps {
  additionalUrlTrackingParams?: { [key: string]: string | number };
  className?: string;
  hasTabbyGateway: boolean;
  hotel: Hotel;
  hotelBookingRoomTypes: HotelBookingRoomTypes | undefined;
  isGroupSearch: boolean;
  isLoadingRates: boolean;
  localeParam: string | undefined;
  nearestCityByIP: Place;
  onResetRateAmenities(): void;
  pageViewId: string;
  rateAmenities: Array<HotelRateAmenity>;
  rates: Array<MetasearchHotelRate>;
  roomMap: { [key: string]: Room };
  roomSelectionRef: React.RefObject<HTMLDivElement>;
  searchIdParam: string | null;
  searchWithoutExtraParams: string;
  selectedRateAmenityIds: Array<number>;
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
}

const HotelDeals: React.FC<HotelDealsProps> = ({
  additionalUrlTrackingParams,
  className,
  hasTabbyGateway,
  hotel,
  hotelBookingRoomTypes,
  isGroupSearch,
  isLoadingRates,
  localeParam,
  nearestCityByIP,
  onResetRateAmenities,
  pageViewId,
  rateAmenities,
  rates,
  roomMap,
  roomSelectionRef,
  searchIdParam,
  searchWithoutExtraParams,
  selectedRateAmenityIds,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
}): JSX.Element => {
  const { userCentricCoreConfigs } = useContext(GlobalContext);
  const [searchParams] = useSearchParams();

  // Google hotel
  const wegoHotelIdSearchParam = searchParams.get('wego_hotel_id');
  const providerSearchParam = searchParams.get('provider');
  const tsCodeSearchParam = searchParams.get('ts_code');

  const apiBaseUrl = API_BASE_URL;
  const currency = useSelector(getCurrencyState);
  const currentSite: CurrentSite = useSelector(getCurrentSiteState);
  const exchangeRate: ExchangeRate = useSelector(getExchangeRateState);
  const hotelMetaSearch: HotelMetaSearch = useSelector(getHotelMetaSearchState);
  const locale = useSelector(getLocaleState);
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);
  const translations = useSelector(getTranslationsState);
  const user = useSelector(getUserState);

  const [roomTypeSearchQuery, setRoomTypeSearchQuery] = useState<string>('');
  const [isPriceExpanded, setIsPriceExpanded] = useState<boolean>(false);

  const [numberOfDisplayRate, setNumberOfDisplayRate] = useState<{
    [providerCode: string]: number;
  }>({});

  const isEnhancedHotelRates =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === ENHANCED_HOTEL_RATES)?.value ===
    '1';

  const dispatch = useDispatch();

  const locationState = useLocation().state as {
    impressionType: 'filter' | 'page_load';
  };

  const isAgodaGH =
    wegoHotelIdSearchParam &&
    providerSearchParam &&
    window.atob(providerSearchParam) === 'agoda.com' &&
    tsCodeSearchParam === '2f2fc';

  const groupedRatesByProviderCodes = useMemo<
    Array<{ providerCode: string; rates: Array<MetasearchHotelRate> }>
  >(() => {
    const sortRatesWithPriceOptions = sortHotelRates(
      rates,
      'asc',
      priceOptions,
      providerSearchParam,
      wegoHotelIdSearchParam,
    );
    return sortRatesWithPriceOptions.reduce(
      (
        accumulator: Array<{
          providerCode: string;
          rates: Array<MetasearchHotelRate>;
        }>,
        rate: MetasearchHotelRate,
      ) => {
        const providerCode = rate.provider.code;
        const providerCodeGroupIndex = accumulator.findIndex(
          (value) => value.providerCode === providerCode,
        );

        let filterByRoomTypeSearchQuery = true;

        if (roomTypeSearchQuery.length > 0) {
          const room = roomMap[rate.roomIds[0]];

          filterByRoomTypeSearchQuery = room.description
            .toLowerCase()
            .includes(roomTypeSearchQuery.toLowerCase());
        }

        if (filterByRoomTypeSearchQuery) {
          if (providerCodeGroupIndex !== -1) {
            accumulator[providerCodeGroupIndex].rates.push(rate);
          } else {
            const hasPinnedRate =
              wegoHotelIdSearchParam &&
              providerSearchParam &&
              window.atob(providerSearchParam) === providerCode;

            if (hasPinnedRate) {
              accumulator.unshift({ providerCode, rates: [rate] });
            } else {
              accumulator.push({ providerCode, rates: [rate] });
            }
          }
        }

        return accumulator;
      },
      [],
    );
  }, [rates, roomMap, roomTypeSearchQuery, priceOptions]);

  const handleDropdownButtonClick = useCallback<() => void>(() => {
    setIsPriceExpanded(!isPriceExpanded);
  }, []);

  const handlePriceOptionsChanged = useCallback<(isIncludingTax: boolean) => void>(
    (isIncludingTax: boolean) => {
      setIsPriceExpanded(false);

      dispatch(changeIsIncludingTaxOrFee(isIncludingTax));
    },
    [],
  );

  const isPayInInstallmentAllowed = useCallback<(rate: MetasearchHotelRate) => boolean>(
    (rate: MetasearchHotelRate) => {
      if (rate.provider.directBooking && hasTabbyGateway) {
        const rateAmenities$ = rate.rateAmenityIds
          .map((rateAmenityId) =>
            rateAmenities.find((rateAmenity) => rateAmenityId === rateAmenity.id),
          )
          .filter(notUndefinedAndNull);

        return (
          rate.provider.directBooking &&
          !rateAmenities$.some(
            (rateAmenity) =>
              rateAmenity.code === 'PAY_AT_THE_HOTEL' || rateAmenity.code === 'PAY_LATER',
          )
        );
      }
      return false;
    },
    [hasTabbyGateway, rateAmenities],
  );

  const rateHandoffHref = useCallback<(rate: MetasearchHotelRate) => string>(
    (rate: MetasearchHotelRate) => {
      if (rate.provider!.directBooking) {
        return appendSearchParams(
          `${hotelsRoute(currentSite, localeParam)}/booking/checkout${searchWithoutExtraParams}`,
          { search_id: searchIdParam, reference_id: rate.id },
        );
      }
      return appendSearchParams(rate.handoffUrl, {
        ...additionalUrlTrackingParams,
        wc: wegoAnalyticsClientId,
        ws: wegoAnalyticsClientSessionId,
      });
    },
    [
      localeParam,
      searchWithoutExtraParams,
      searchIdParam,
      additionalUrlTrackingParams,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    ],
  );

  const handleShowMoreDealsClick = useCallback<(providerCode: string, totalCount: number) => void>(
    (providerCode: string, totalCount: number) => {
      const previousCount = numberOfDisplayRate[providerCode] ?? 0;
      const numberExpandDisplayRate = {
        ...numberOfDisplayRate,
        [providerCode]: isEnhancedHotelRates
          ? totalCount
          : Math.min(previousCount + 10, totalCount),
      };
      setNumberOfDisplayRate(numberExpandDisplayRate);

      const displayedRates = groupedRatesByProviderCodes
        .map((groupedRate) =>
          groupedRate.rates.slice(0, numberExpandDisplayRate[groupedRate.providerCode] ?? 1),
        )
        .flat();

      impressionLogHotelDetail(
        'deals_interaction',
        apiBaseUrl,
        wegoAnalyticsClientId!,
        wegoAnalyticsClientSessionId!,
        null,
        displayedRates,
        priceOptions,
        pageViewId,
        searchIdParam!,
        user?.userHash,
      );
    },
    [
      numberOfDisplayRate,
      groupedRatesByProviderCodes,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
      priceOptions,
      pageViewId,
      searchIdParam,
      user,
      isEnhancedHotelRates,
    ],
  );

  const handleHideAllDealsClick = useCallback<(providerCode: string) => void>(
    (providerCode: string) => {
      const cloneNumberOfDisplayRate = { ...numberOfDisplayRate };

      delete cloneNumberOfDisplayRate[providerCode];
      setNumberOfDisplayRate(cloneNumberOfDisplayRate);

      const displayedRates = groupedRatesByProviderCodes
        .map((groupedRate) =>
          groupedRate.rates.slice(0, cloneNumberOfDisplayRate[groupedRate.providerCode] ?? 1),
        )
        .flat();

      impressionLogHotelDetail(
        'deals_interaction',
        apiBaseUrl,
        wegoAnalyticsClientId!,
        wegoAnalyticsClientSessionId!,
        null,
        displayedRates,
        priceOptions,
        pageViewId,
        searchIdParam!,
        user?.userHash,
      );
    },
    [
      numberOfDisplayRate,
      groupedRatesByProviderCodes,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
      priceOptions,
      pageViewId,
      searchIdParam,
      user,
    ],
  );

  const handleResetRateAmenities = useCallback<() => void>(() => {
    if (roomTypeSearchQuery.length > 0) {
      setRoomTypeSearchQuery('');
    }
    onResetRateAmenities();
  }, [onResetRateAmenities, roomTypeSearchQuery]);

  const handleRateClick = useCallback<(selectedRate: MetasearchHotelRate) => void>(
    (selectedRate) => {
      const isRoomTypeExist = hotelBookingRoomTypes?.data.rooms.find(
        (room) => selectedRate.params.room_type_id === room.typeId,
      );

      if (selectedRate.provider.directBooking && isRoomTypeExist && roomSelectionRef.current) {
        window.scrollTo({
          behavior: 'smooth',
          // 113 is the height of sticky search form,
          // 164 is the height of sticky HotelAnchorTabs,
          // 68 is height of HotelStickyRatesFilter
          // 48 is the marginTop of the <ChooseYourRoom /> component
          top: roomSelectionRef.current.offsetTop - 113 - 164 - 68 + 48,
        });
      } else {
        window.open(rateHandoffHref(selectedRate), '_blank', 'noreferrer');
      }

      const displayedRates = groupedRatesByProviderCodes
        .map((groupedRate) =>
          groupedRate.rates.slice(0, numberOfDisplayRate[groupedRate.providerCode] ?? 1),
        )
        .flat();
      const selectedRateIndex = displayedRates.findIndex((rate) => rate.id === selectedRate.id);

      impressionLogOnHotelRateClick(
        apiBaseUrl,
        wegoAnalyticsClientId!,
        wegoAnalyticsClientSessionId!,
        null,
        groupedRatesByProviderCodes[0].rates[0],
        selectedRate,
        priceOptions,
        pageViewId,
        searchIdParam!,
        selectedRateIndex + 1,
        user?.userHash,
      );

      if (!!nearestCityByIP) {
        webEngageTrackRateClick(
          currentSite.countryCode,
          locale,
          nearestCityByIP,
          hotelMetaSearch.search!,
          window.location.href,
          hotel!,
          selectedRate,
        );
      }
      if (!selectedRate.provider.directBooking) {
        webEngageTrackPurchase();
      }
    },
    [
      groupedRatesByProviderCodes,
      numberOfDisplayRate,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    ],
  );

  // Set all picker focus to false if body is clicked
  useEffect(() => {
    if (isPriceExpanded) {
      const handleBodyOnClick = (): void => {
        if (isPriceExpanded) {
          setIsPriceExpanded(false);
        }
      };

      document.body.addEventListener('click', handleBodyOnClick);

      return () => {
        document.body.removeEventListener('click', handleBodyOnClick);
      };
    }
  }, [isPriceExpanded]);

  // Default expand grouped rate if there is only one group.
  useEffect(() => {
    if (isAgodaGH && groupedRatesByProviderCodes.length > 0) {
      setNumberOfDisplayRate({
        [groupedRatesByProviderCodes[0].providerCode]: groupedRatesByProviderCodes[0].rates.length,
      });
    } else if (
      Object.keys(numberOfDisplayRate).length === 0 &&
      groupedRatesByProviderCodes.length === 1 &&
      !isEnhancedHotelRates
    ) {
      const metaProvider = !groupedRatesByProviderCodes[0].rates[0].provider.directBooking;
      if (metaProvider) {
        setNumberOfDisplayRate({ [groupedRatesByProviderCodes[0].providerCode]: 10 });
      }
    } else if (groupedRatesByProviderCodes.length > 1) {
      setNumberOfDisplayRate({});
    }
  }, [groupedRatesByProviderCodes.length, isEnhancedHotelRates, isAgodaGH]);

  // Impression log (when page load and rate amenities filter)
  useEffect(() => {
    const timer = locationState?.impressionType === 'filter' ? 1000 : 0;
    const impressionLogTimer = setTimeout(() => {
      if (
        !!wegoAnalyticsClientId &&
        !!wegoAnalyticsClientSessionId &&
        searchIdParam &&
        !!locationState?.impressionType
      ) {
        const displayedRates = groupedRatesByProviderCodes
          .map((groupedRate) =>
            groupedRate.rates.slice(0, numberOfDisplayRate[groupedRate.providerCode] ?? 1),
          )
          .flat();

        impressionLogHotelDetail(
          locationState?.impressionType,
          apiBaseUrl,
          wegoAnalyticsClientId,
          wegoAnalyticsClientSessionId,
          null,
          displayedRates,
          priceOptions,
          pageViewId,
          searchIdParam,
          user?.userHash,
        );
      }
    }, timer);

    return () => clearTimeout(impressionLogTimer);
  }, [groupedRatesByProviderCodes.length, locationState?.impressionType]);

  return (
    <div className={clsx(styles.container, className, isEnhancedHotelRates && styles.enhanced)}>
      {!isEnhancedHotelRates ? (
        <div className={styles.headers}>
          <div className={clsx(styles.header, styles.bookingSites)}>
            <label>{translations.booking_sites}</label>
            <div className={styles.inputContainer}>{translations.booking_options}</div>
          </div>

          <div className={clsx(styles.header, styles.roomTypes)}>
            <label>{translations.room_types}</label>
            <div className={styles.inputContainer}>
              <input
                autoComplete='off'
                id='roomType'
                inputMode='text'
                name='roomType'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRoomTypeSearchQuery(e.target.value)
                }
                placeholder={((translations.all_room_types as string) || '').slice(0, -1)}
                spellCheck={false}
                type='text'
                value={roomTypeSearchQuery}
              />

              <SearchIcon className={styles.icon} />
            </div>
          </div>

          <div
            className={clsx(styles.header, styles.price, isPriceExpanded && styles.expanded)}
            onClick={handleDropdownButtonClick}
          >
            <label>{translations.price}</label>
            <div className={styles.inputContainer}>
              <i className={clsx(styles.triangle, styles.down)}></i>
              {priceOptions.isIncludingTaxOrFee
                ? translations.include_tax
                : translations.exclude_tax}
            </div>

            {/* Price options dropdown */}
            {isPriceExpanded ? (
              <div className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
                <div
                  className={clsx(styles.item, priceOptions.isIncludingTaxOrFee && styles.active)}
                  onClick={() => handlePriceOptionsChanged(true)}
                >
                  {translations.include_tax}
                </div>
                <div
                  className={clsx(styles.item, !priceOptions.isIncludingTaxOrFee && styles.active)}
                  onClick={() => handlePriceOptionsChanged(false)}
                >
                  {translations.exclude_tax}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {groupedRatesByProviderCodes.map((groupedRatesByProviderCode) => (
        <div key={groupedRatesByProviderCode.providerCode} className={styles.groupedRates}>
          {groupedRatesByProviderCode.rates
            .slice(0, numberOfDisplayRate[groupedRatesByProviderCode.providerCode] ?? 1)
            .map((rate, index) => {
              const distinctRoomIds = uniq(rate.roomIds);

              const rateAmenities$ = rate.rateAmenityIds
                .map((rateAmenityId) =>
                  rateAmenities.find((rateAmenity) => rateAmenityId === rateAmenity.id),
                )
                .filter(notUndefinedAndNull)
                .sort((a, b) => a.id - b.id);

              const nonPaymentTypeRateAmenities = rateAmenities$.filter(
                (rateAmenity) => rateAmenity.type !== 'PAYMENT',
              );
              const paymentTypeRateAmenities = rateAmenities$
                .filter(
                  (rateAmenity) =>
                    rateAmenity.type === 'PAYMENT' && rateAmenity.code !== 'NO_CREDIT_CARD_NEEDED',
                )
                .slice(0, 2);

              // Show more when there is only one meta provider
              const showMoreDeals =
                !rate.provider.directBooking &&
                Object.keys(numberOfDisplayRate).length === 0 &&
                groupedRatesByProviderCodes.length === 1;

              return (
                <div key={rate.id} className={styles.rate}>
                  {/* Provider logo */}
                  <div
                    className={clsx(
                      styles.providerLogoContainer,
                      isEnhancedHotelRates && styles.logoMinWidth,
                    )}
                  >
                    <img
                      className={styles.providerLogo}
                      src={`https://assets.wego.com/image/upload/c_fit,h_90,f_auto,q_auto/${getDailyVersion()}/providers/rectangular_logos/${
                        rate.provider.code
                      }.png`}
                    />
                  </div>

                  {/* Description */}
                  <div className={styles.descriptionContainer}>
                    {/* Room description (not is group search) */}
                    {!isGroupSearch &&
                    distinctRoomIds.length > 0 &&
                    !!roomMap[distinctRoomIds[0]] ? (
                      <div className={styles.roomDescription}>
                        {roomMap[distinctRoomIds[0]].description}
                      </div>
                    ) : null}

                    {/* Room description (is group search) */}
                    {isGroupSearch
                      ? distinctRoomIds.map((distinctRoomId) => {
                          const room: Room | undefined = roomMap[distinctRoomId];

                          if (!!room) {
                            const roomCount = rate.roomIds.filter(
                              (roomId) => roomId === distinctRoomId,
                            ).length;

                            return (
                              <div key={distinctRoomId} className={styles.roomDescription}>
                                {translateNumber(roomCount, locale === 'fa')} x {room.description}
                                {!!room.roomSizeInM2 ? (
                                  <div className={styles.roomSize}>
                                    {translateNumber(room.roomSizeInM2, locale === 'fa')}{' '}
                                    {translations.square_meters}
                                  </div>
                                ) : null}
                                {!!room.maxOccupancy ? (
                                  <div className={styles.maxOccupancy}>
                                    {translations.max_hotel}:{' '}
                                    {room.maxOccupancy > 4
                                      ? `${translateNumber(room.maxOccupancy, locale === 'fa')} x `
                                      : null}{' '}
                                    {room.maxOccupancy > 4 ? (
                                      <PersonIcon className={styles.icon} />
                                    ) : null}
                                    {room.maxOccupancy < 5
                                      ? [...Array(room.maxOccupancy).keys()].map((value) => (
                                          <PersonIcon key={value} className={styles.icon} />
                                        ))
                                      : null}
                                  </div>
                                ) : null}
                              </div>
                            );
                          }
                        })
                      : null}

                    {/* Room left */}
                    {!!rate.remainingRoomsCount ? (
                      <div className={styles.roomLeft}>
                        {translateText(
                          translations.hotel_room_left,
                          locale,
                          rate.remainingRoomsCount,
                        )}
                      </div>
                    ) : null}

                    {/* Non-payment type rate amenities */}
                    {nonPaymentTypeRateAmenities.length > 0 && !isEnhancedHotelRates ? (
                      <div className={styles.rateAmenities}>
                        {nonPaymentTypeRateAmenities.map((nonPaymentTypeRateAmenity) => (
                          <div
                            key={nonPaymentTypeRateAmenity.id}
                            className={clsx(
                              styles.rateAmenity,
                              selectedRateAmenityIds.includes(nonPaymentTypeRateAmenity.id) &&
                                styles.selected,
                            )}
                          >
                            {nonPaymentTypeRateAmenity.name}
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {rateAmenities$.length > 0 && isEnhancedHotelRates ? (
                      <div className={styles.rateAmenities}>
                        {rateAmenities$.map((nonPaymentTypeRateAmenity) => (
                          <div
                            key={nonPaymentTypeRateAmenity.id}
                            className={clsx(
                              styles.rateAmenity,
                              selectedRateAmenityIds.includes(nonPaymentTypeRateAmenity.id) &&
                                styles.selected,
                            )}
                          >
                            {nonPaymentTypeRateAmenity.name}
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {/* Promotion */}
                    {!!rate.promos
                      ? rate.promos.map((promo: HotelPromo, index: number) => (
                          <Promo
                            key={index}
                            promoCode={promo.code}
                            promoLabel={promo.shortMessage}
                            promoDescription={promo.desc}
                            translations={translations}
                          />
                        ))
                      : null}

                    {/* Show more deals */}
                    {showMoreDeals && !isEnhancedHotelRates ? (
                      <div className={styles.showOrHideDealsToggleContainer}>
                        <div
                          className={styles.showOrHideDealsToggle}
                          onClick={() =>
                            handleShowMoreDealsClick(
                              groupedRatesByProviderCode.providerCode,
                              groupedRatesByProviderCode.rates.length,
                            )
                          }
                        >
                          {`${translations.show_more_deals} (${translateNumber(
                            groupedRatesByProviderCode.rates.length - 1,
                            locale === 'fa',
                          )})`}
                          <i className={clsx(styles.chevron, styles.bottom)}></i>
                        </div>
                      </div>
                    ) : null}

                    {/* Hide all deals */}
                    {index === 0 &&
                    !!numberOfDisplayRate[groupedRatesByProviderCode.providerCode] &&
                    !isEnhancedHotelRates &&
                    !isAgodaGH ? (
                      <div className={styles.showOrHideDealsToggleContainer}>
                        <div
                          className={styles.showOrHideDealsToggle}
                          onClick={() =>
                            handleHideAllDealsClick(groupedRatesByProviderCode.providerCode)
                          }
                        >
                          {translations.hide_all_deals}
                          <i className={clsx(styles.chevron, styles.top)}></i>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Price */}
                  <div className={styles.priceContainer}>
                    <div className={styles.priceAndViewDeal}>
                      <div
                        className={clsx(
                          styles.viewDeal,
                          styles.darkGreenBackground,
                          !rate.provider!.directBooking && styles.orangeBackground,
                        )}
                        onClick={() => handleRateClick(rate)}
                      >
                        {isEnhancedHotelRates ? (
                          <span>{translations.Select}</span>
                        ) : rate.provider!.directBooking ? (
                          hotelBookingRoomTypes?.data.rooms.find(
                            (room) => rate.params.room_type_id === room.typeId,
                          ) ? (
                            <span>{translations.Select}</span>
                          ) : (
                            translations.book_now
                          )
                        ) : (
                          translations.view_deal
                        )}
                      </div>

                      <div>
                        <Price
                          className={clsx(styles.price, isEnhancedHotelRates && styles.priceAlign)}
                          currency={currency}
                          exchangeRate={exchangeRate}
                          isInline
                          locale={locale}
                          price={convertHotelRatePriceToPrice(
                            rate.price,
                            priceOptions.showTotalPrice,
                            priceOptions.isIncludingTaxOrFee,
                          )}
                        />
                        {isEnhancedHotelRates ? (
                          <div className={styles.priceType}>
                            {priceOptions.isIncludingTaxOrFee
                              ? translations.include_tax_fee
                              : translations.exclude_tax_fee}
                          </div>
                        ) : (
                          <div className={styles.priceType}>
                            {priceOptions.showTotalPrice
                              ? translations.total_stay
                              : translations.hotel_per_night}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Payment type rate amenities */}
                    {(paymentTypeRateAmenities.length > 0 || isPayInInstallmentAllowed(rate)) &&
                    !isEnhancedHotelRates ? (
                      <div className={styles.rateAmenities}>
                        {paymentTypeRateAmenities.map((paymentTypeRateAmenity) => (
                          <div
                            key={paymentTypeRateAmenity.id}
                            className={clsx(
                              styles.rateAmenity,
                              selectedRateAmenityIds.includes(paymentTypeRateAmenity.id) &&
                                styles.selected,
                            )}
                          >
                            {paymentTypeRateAmenity.name}
                          </div>
                        ))}

                        {isPayInInstallmentAllowed(rate) ? (
                          <div
                            className={clsx(
                              styles.rateAmenity,
                              selectedRateAmenityIds.includes(10001) && styles.selected,
                            )}
                          >
                            <img
                              src={cloudinaryImageTransformations(
                                'https://assets.wego.com/image/upload/v1663418328/web/payment_cards/tabby_square_3x.png',
                                'c_fill',
                                'fl_lossy',
                                'q_auto:low',
                                'f_auto',
                                'w_48',
                                'h_48',
                              )}
                            />
                            <div>{translations.lbl_interest_free_payments}</div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}

          {/* Show more deals v2 */}
          {Object.keys(numberOfDisplayRate).length === 0 &&
          groupedRatesByProviderCodes.length === 1 &&
          !groupedRatesByProviderCode?.rates[0]?.provider.directBooking &&
          isEnhancedHotelRates ? (
            <div className={styles.showMoreSection}>
              <div
                className={styles.showMoreCtaWrapper}
                onClick={() =>
                  handleShowMoreDealsClick(
                    groupedRatesByProviderCode.providerCode,
                    groupedRatesByProviderCode.rates.length,
                  )
                }
              >
                <p className={styles.showMoreCta}>
                  {translateText(
                    translations.view_all_rooms_from,
                    locale,
                    groupedRatesByProviderCode.rates.length,
                    groupedRatesByProviderCodes[0]?.rates[0]?.provider.name,
                  )}
                </p>
                <Chevron className={clsx(styles.chevron)} />
              </div>
            </div>
          ) : null}

          {!!numberOfDisplayRate[groupedRatesByProviderCode.providerCode] &&
          isEnhancedHotelRates ? (
            <div className={styles.showMoreSection}>
              <div
                className={styles.showMoreCtaWrapper}
                onClick={() => handleHideAllDealsClick(groupedRatesByProviderCode.providerCode)}
              >
                <p className={styles.showMoreCta}>{translations.hide_all}</p>
                <Chevron className={clsx(styles.chevron, styles.up)} />
              </div>
            </div>
          ) : null}

          {/* Show more deals */}
          {!!numberOfDisplayRate[groupedRatesByProviderCode.providerCode] &&
          numberOfDisplayRate[groupedRatesByProviderCode.providerCode] <
            groupedRatesByProviderCode.rates.length &&
          !isEnhancedHotelRates ? (
            <div
              className={styles.showMoreDeals}
              onClick={() =>
                handleShowMoreDealsClick(
                  groupedRatesByProviderCode.providerCode,
                  groupedRatesByProviderCode.rates.length,
                )
              }
            >
              {`${translations.show_more_deals} (${translateNumber(
                groupedRatesByProviderCode.rates.length -
                  numberOfDisplayRate[groupedRatesByProviderCode.providerCode],
                locale === 'fa',
              )})`}
              <i className={clsx(styles.chevron, styles.bottom)}></i>
            </div>
          ) : null}
        </div>
      ))}

      {/* Skeleton */}
      {isLoadingRates && groupedRatesByProviderCodes.length === 0 ? (
        <div className={styles.skeletonContainer}>
          <div className={styles.providerLogoContainer}>
            <div className={styles.skeleton}></div>
          </div>

          <div className={styles.descriptionContainer}>
            <div className={clsx(styles.skeleton, styles.description)}></div>
            <div className={clsx(styles.skeleton, styles.amenities)}></div>
          </div>

          <div className={styles.priceContainer}>
            <div>
              <div
                className={clsx(
                  styles.skeleton,
                  styles.price,
                  isEnhancedHotelRates && styles.priceAlign,
                )}
              ></div>
              {isEnhancedHotelRates ? (
                <div className={styles.priceType}>
                  {priceOptions.isIncludingTaxOrFee
                    ? translations.include_tax_fee
                    : translations.exclude_tax_fee}
                </div>
              ) : (
                <div className={styles.priceType}>
                  {priceOptions.showTotalPrice
                    ? translations.total_stay
                    : translations.hotel_per_night}
                </div>
              )}
            </div>
            <div className={clsx(styles.skeleton, styles.viewDeal)}></div>
          </div>
        </div>
      ) : null}

      {/* No Rates */}
      {!isLoadingRates && rateAmenities.length === 0 && groupedRatesByProviderCodes.length === 0 ? (
        <div className={styles.noRates}>
          {translations.no_rooms_available} {translations.try_different_dates}
        </div>
      ) : null}

      {/* No results (after user selected with multiple rateAmenities) */}
      {!isLoadingRates && rateAmenities.length > 0 && groupedRatesByProviderCodes.length === 0 ? (
        <NoResults
          buttonLabel={translations.reset as string}
          className={styles.noResult}
          message={translations.no_results as string}
          onClick={handleResetRateAmenities}
          title={translations.no_result_found as string}
        />
      ) : null}
    </div>
  );
};

export default HotelDeals;
